/**
 * A small history-API router: `useRoute()` gives the current path + query, `navigate()` changes
 * it, `<Link>` renders anchors that stay in the app. No dependency; the app's route table is data
 * (navigation registry + entity routes) so a pattern-matching router adds nothing.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

export interface Route { path: string; query: URLSearchParams; hash: string }
interface RouterApi extends Route { navigate: (to: string, opts?: { replace?: boolean }) => void; setQuery: (patch: Record<string, string | number | null | undefined>, opts?: { replace?: boolean }) => void }

const RouterContext = createContext<RouterApi | null>(null);

function read(): Route {
  return { path: window.location.pathname.replace(/\/+$/, '') || '/', query: new URLSearchParams(window.location.search), hash: window.location.hash };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(read);
  useEffect(() => {
    const onPop = () => setRoute(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = useCallback((to: string, opts: { replace?: boolean } = {}) => {
    if (opts.replace) window.history.replaceState(null, '', to); else window.history.pushState(null, '', to);
    setRoute(read());
    if (!opts.replace) window.scrollTo({ top: 0 });
  }, []);
  const setQuery = useCallback((patch: Record<string, string | number | null | undefined>, opts: { replace?: boolean } = { replace: true }) => {
    const q = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) { if (v === null || v === undefined || v === '') q.delete(k); else q.set(k, String(v)); }
    const s = q.toString();
    navigate(`${window.location.pathname}${s ? `?${s}` : ''}`, opts);
  }, [navigate]);
  const value = useMemo(() => ({ ...route, navigate, setQuery }), [route, navigate, setQuery]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRoute(): RouterApi {
  const r = useContext(RouterContext);
  if (!r) throw new Error('useRoute outside RouterProvider');
  return r;
}

export function Link({ to, replace, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }) {
  const { navigate } = useRoute();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || rest.target === '_blank') return;
    e.preventDefault();
    navigate(to, { replace });
  };
  return <a href={to} onClick={handle} {...rest} />;
}

/** True when `path` equals or starts with `prefix` as a path segment boundary. */
export function isActive(path: string, prefix: string): boolean {
  if (prefix === '/') return path === '/';
  return path === prefix || path.startsWith(`${prefix}/`);
}
